import { ILessons, ILessonsNodes } from '@dtos/Course'
import { Slider } from '@components/Slider'
import { LessonTitleLink } from './LessonTitleLink'
import { ExtraContentCard } from './ExtraContentCard'
import { isContentAvailable } from '@utils/contentAvailable'
import { lessonExtraContentAvailability } from '@utils/lessonExtraContentAvailability'
import { GestureReponderEvent } from '@tamagui/core'
import { IOpenModalMediaProps } from './ExtraContentContent'
import { View, isWeb } from 'tamagui'
import { useEffect, useRef, useState } from 'react'
import { InView } from '@components/IntersectionObserver'
import { router } from 'expo-router'
import { useCurrentLesson } from '@hooks/useCurrentLesson'
import { useCourse } from '@hooks/useCourse'
import { useEnrollment } from '@hooks/useEnrollment'
import { useHasFeature } from '@hooks/useThemeParams'
import { MEMBERS_PAGE_COURSE_CONTENTS } from '@constants/features'
import {
  courseContentPath,
  heroThemeCourseContentPath,
} from '@utils/coursePaths'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { useCourseRouteParams } from '@contexts/CourseRouteParamsContext'

interface IExtraContentLessonSliderProps {
  lesson: ILessonsNodes
  perView: number
  enrollmentActivatedAt: string
  handlePressDownload: (
    e: GestureReponderEvent,
    link: string | null | undefined,
  ) => void
  handlePressOpenModal: (
    media: IOpenModalMediaProps,
    index: number,
    isMain: boolean,
    nodes: number,
  ) => void
  isLastLesson: boolean
  loadMoreLessonsExtraContents: () => Promise<void>
}

export function ExtraContentLessonSlider({
  lesson,
  perView,
  enrollmentActivatedAt,
  handlePressOpenModal,
  handlePressDownload,
  isLastLesson,
  loadMoreLessonsExtraContents,
}: IExtraContentLessonSliderProps) {
  const lessonRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const { course } = useCourse()
  const { enrollment } = useEnrollment()
  const { enrollmentId, courseId } = useCourseRouteParams()
  const { setCurrentLesson } = useCurrentLesson()
  const hasCourseContentsPage = useHasFeature(MEMBERS_PAGE_COURSE_CONTENTS)
  const { isPreview } = useAdminPreview()
  const [loadedMoreLessonsExtraContents, setLoadedMoreLessonsExtraContents] =
    useState(false)

  const lessonLiberationData = {
    releaseAt: lesson.releaseAt,
    releaseAfter: lesson.releaseAfter,
    lockAt: lesson.lockAt,
    lockAfter: lesson.lockAfter,
    createdAt: enrollmentActivatedAt,
    courseClassRelease: enrollment?.courseClassRelease,
    courseClassReleaseOffset: lesson.courseClassReleaseOffset,
  }
  const isLessonAvailable = isContentAvailable(lessonLiberationData)
  const canLoadMoreLessons =
    isVisible && isLastLesson && !loadedMoreLessonsExtraContents

  const handleLessonTitlePress = (lesson: ILessons['nodes'][0]) => {
    if (course?.linearRequirements) return
    setCurrentLesson(lesson)

    // Escolas sem a feature members_page_course_contents não têm a página de
    // consumo no SPA de membros — navegar via router deixaria a URL 404 no
    // refresh. No web, redireciona (full load) para a página legada do
    // hero_theme (sem /online_course). No nativo, sempre usa o router.
    if (isWeb && !isPreview && !hasCourseContentsPage) {
      window.location.href = heroThemeCourseContentPath({
        enrollmentId,
        courseId,
        courseContentId: lesson.courseContentId,
      })
      return
    }

    const formattedPath = courseContentPath({
      isPreview,
      enrollmentId,
      courseId,
      courseContentId: lesson.courseContentId,
    })
    router.navigate(formattedPath)
  }

  useEffect(() => {
    if (canLoadMoreLessons) {
      loadMoreLessonsExtraContents()
      setLoadedMoreLessonsExtraContents(true)
    }
  }, [canLoadMoreLessons, loadMoreLessonsExtraContents])

  const handleInViewChange = (inView: boolean) => {
    if (inView && !isVisible) {
      setIsVisible(true)
    }
  }

  if (lesson.extraMedias.length === 0 || !isLessonAvailable) return null
  return (
    <InView onChange={(inView: boolean) => handleInViewChange(inView)}>
      <View ref={lessonRef} mb="$size.spacing-md">
        <Slider
          slidesTotalNumber={lesson.extraMedias.length}
          slidesPerView={perView}
          displaySliderControls={lesson.extraMedias.length > perView}
          sliderTitle={
            <LessonTitleLink
              disabled={Boolean(course?.linearRequirements)}
              handlePress={() => handleLessonTitlePress(lesson)}
              title={lesson.title}
            />
          }
          marginBottomToSliderControls="$spacing-inset-xs"
        >
          {lesson.extraMedias.map((extraContent, index) => (
            <ExtraContentCard
              key={extraContent.id}
              extraContent={{
                title: extraContent.title,
                media: extraContent,
                id: extraContent.id,
                ...lessonLiberationData,
                downloadable: true,
              }}
              isLessonAvailable={isContentAvailable(lessonLiberationData)}
              availability={lessonExtraContentAvailability(
                lesson,
                extraContent.id,
                {
                  activatedAt: enrollmentActivatedAt,
                  courseClassRelease: enrollment?.courseClassRelease,
                },
              )}
              handlePressDownload={handlePressDownload}
              handlePressOpenModal={() =>
                handlePressOpenModal(
                  extraContent,
                  index,
                  false,
                  lesson.extraMedias.length,
                )
              }
            />
          ))}
        </Slider>
      </View>
    </InView>
  )
}
